import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { LANG, t } from "@/lib/i18n";

/**
 * Word → digit map for spoken numbers ("three point eight" → 3.8).
 * Supports up to two decimal places and integer parts up to ~999.
 * Not exhaustive — good enough for pCi/L readings which are typically 0-100.
 */
const WORD_TO_NUMBER = {
  zero: 0, oh: 0, o: 0,
  one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14,
  fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
  twenty: 20, thirty: 30, forty: 40, fifty: 50,
  sixty: 60, seventy: 70, eighty: 80, ninety: 90,
  hundred: 100,
  // Spanish
  cero: 0, un: 1, uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7,
  ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12, trece: 13, catorce: 14, quince: 15,
  dieciseis: 16, diecisiete: 17, dieciocho: 18, diecinueve: 19,
  veinte: 20, veintiun: 21, veintiuno: 21, veintiuna: 21, veintidos: 22, veintitres: 23, veinticuatro: 24,
  veinticinco: 25, veintiseis: 26, veintisiete: 27, veintiocho: 28, veintinueve: 29,
  treinta: 30, cuarenta: 40, cincuenta: 50, sesenta: 60, setenta: 70, ochenta: 80, noventa: 90,
  cien: 100, ciento: 100,
};

// Lowercase and strip accents so "dieciséis" / "sótano" match plain keys.
const normalize = (s) => String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Fuzzy-match a spoken transcript to one of the given options.
 * Each option: { value, label, synonyms?: string[] }.
 * Returns the option value or null.
 */
export function matchSpokenOption(transcript, options) {
  if (!transcript || !options?.length) return null;
  const t = normalize(transcript).replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
  if (!t) return null;

  // 1) Exact / substring match against label or synonyms
  for (const o of options) {
    const label = normalize(o.label || "");
    const syns = (o.synonyms || []).map((s) => normalize(s));
    const candidates = [label, ...syns];
    for (const c of candidates) {
      if (!c) continue;
      if (t === c) return o.value;
      if (t.includes(c) || c.includes(t)) return o.value;
    }
  }

  // 2) Token overlap fallback — pick the option with the most matching tokens
  const tokens = new Set(t.split(" ").filter(Boolean));
  let best = null;
  let bestScore = 0;
  for (const o of options) {
    const candidates = [String(o.label || ""), ...(o.synonyms || [])];
    let score = 0;
    for (const c of candidates) {
      for (const w of normalize(c).replace(/[^\w\s]/g, " ").split(" ").filter(Boolean)) {
        if (tokens.has(w)) score += 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      best = o.value;
    }
  }
  return bestScore > 0 ? best : null;
}

export function parseSpokenNumber(input) {
  if (!input) return null;
  const clean = normalize(input).replace(/(\d),(\d)/g, "$1.$2").replace(/[^\w\s.]/g, " ").trim();

  // Case 1: already contains a digit — extract e.g. "3.8", ".9", "12"
  const digitMatch = clean.match(/(\d+(?:\.\d+)?|\.\d+)/);
  if (digitMatch) return parseFloat(digitMatch[1]);

  // Case 2: word-based — split around "point"/"decimal"
  // Support leading "point nine" by splitting on optional leading + internal delimiter.
  const parts = clean.split(/(?:^|\s+)(?:point|decimal|dot|punto|coma)(?:\s+|$)/);
  // Handle "point nine" — starts with "point ..." (no leading integer words)
  const startsWithPoint = /^(?:point|decimal|dot|punto|coma)(?:\s+|$)/.test(clean);
  const wordsToInt = (words) => {
    let sum = 0, current = 0;
    for (const w of words) {
      const n = WORD_TO_NUMBER[w];
      if (n == null) continue;
      if (n === 100) current = (current || 1) * 100;
      else current += n;
    }
    sum += current;
    return sum;
  };
  const wordsToDecDigits = (words) =>
    words
      .map((w) => WORD_TO_NUMBER[w])
      .filter((n) => n != null && n < 10)
      .join("");

  if (parts.length === 1) {
    const int = wordsToInt(parts[0].split(/\s+/));
    return isFinite(int) && int > 0 ? int : null;
  }
  const int = startsWithPoint ? 0 : wordsToInt(parts[0].split(/\s+/));
  const dec = wordsToDecDigits(parts[1].split(/\s+/));
  if (!dec) return isFinite(int) ? int : null;
  const combined = parseFloat(`${int}.${dec}`);
  return isFinite(combined) ? combined : null;
}

const getRecognizer = () => {
  if (typeof window === "undefined") return null;
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
};

/**
 * Reusable mic toggle. Renders JUST the button — the parent controls layout.
 * `mode`: "number" parses spoken numbers into a float;
 *         "text" appends transcript;
 *         "select" fuzzy-matches transcript to one of `options`.
 * When mode="select", pass `options` = [{value, label, synonyms?: []}, ...].
 */
const VoiceInputButton = ({ mode = "text", onResult, options, testid = "voice-mic-button" }) => {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recRef = useRef(null);

  useEffect(() => {
    const rec = getRecognizer();
    if (!rec) { setSupported(false); return; }
    rec.lang = LANG === "es" ? "es-US" : "en-US";
    rec.interimResults = false;
    rec.continuous = false;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      const transcript = Array.from(e.results)
        .map((r) => r[0]?.transcript || "")
        .join(" ")
        .trim();
      if (!transcript) return;
      if (mode === "number") {
        const n = parseSpokenNumber(transcript);
        if (n != null) onResult(n, transcript);
      } else if (mode === "select") {
        const v = matchSpokenOption(transcript, options || []);
        if (v != null) onResult(v, transcript);
      } else {
        onResult(transcript, transcript);
      }
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);

    recRef.current = rec;
    return () => { try { rec.abort(); } catch (_) {} };
  }, [mode, onResult, options]);

  if (!supported) return null;

  const toggle = () => {
    const rec = recRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch (_) {}
      setListening(false);
    } else {
      try {
        rec.start();
        setListening(true);
      } catch (_) {
        // "already started" if user double-clicked
      }
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={listening ? t("Stop dictation") : t("Start dictation")}
      aria-pressed={listening}
      className={`inline-flex items-center justify-center rounded-full w-9 h-9 transition-colors ${
        listening
          ? "bg-[#9B2C2C] text-white animate-pulse"
          : "border border-[#E8ECEA] text-[#2F524A] hover:bg-[#F4F7F6]"
      }`}
      data-testid={testid}
      data-listening={listening}
    >
      {listening ? <MicOff size={16} /> : <Mic size={16} />}
    </button>
  );
};

export default VoiceInputButton;
